/* Fine Foams cart resilience layer. Must load BEFORE cart.js.

   Fixes two live mobile bugs (8 Aug 2026):

   1. "There was an error while updating your cart. Please try again."
      Several independent code paths write to the cart — Dawn's quantity/remove
      controls, the PDP free-gift add (ff-product.js), the drawer upsell
      (ff-cart.js) and Shopify Standard Actions. None of them coordinate, so
      two writes could overlap. Because the theme addresses lines by POSITION,
      the second write arrived after positions had shifted and Shopify answered
      422 "line parameter is invalid" — which Dawn surfaced as a failure even
      though the cart was usually correct. We serialise every cart write so an
      overlap cannot happen in the first place.

   2. Tapping the cart icon sometimes navigated to /cart instead of opening the
      drawer. The icon is an <a href="/cart"> whose handler is bound by
      cart-drawer.js in its connectedCallback; until that custom element
      upgrades (slower on mobile) or if it threw, the tap just followed the
      link. We bind our own capture-phase handler immediately, so the drawer
      always wins once it exists.
*/
(function () {
  if (window.__ffCartGuard) return;
  window.__ffCartGuard = true;

  /* ── 1. Serialise cart writes ─────────────────────────────────────────── */

  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var CART_CHANGE = /\/cart\/change(\.js)?(\?|$)/;
  var QUEUE_TIMEOUT_MS = 10000;
  var nativeFetch = window.fetch.bind(window);
  var chain = Promise.resolve();
  var generation = 0; // bumped after every cart write that lands
  var inFlight = 0; // writes queued or running — the supervisor must not render while > 0
  var lastTouch = 0; // last user interaction with cart controls (epoch ms)

  function noop() {}

  function afterAtMost(promise, ms) {
    // The queue must never deadlock behind a request that never settles.
    return Promise.race([
      promise.then(noop, noop),
      new Promise(function (resolve) { setTimeout(resolve, ms); })
    ]);
  }

  /* Line keys on this store are NOT stable: the free-gift discount reallocates
     on every cart change, so Shopify rehashes the surviving lines' keys. A
     queued request therefore holds a key that no longer exists by the time it
     runs, and its action would be silently dropped. The key's prefix is the
     variant id ("47344110272768:hash"), so we can re-find the line by variant
     and rewrite the request to the line's current key. */
  function reResolve(init) {
    var body;
    try { body = JSON.parse(init.body); } catch (e) { return Promise.resolve(init); }
    if (!body || typeof body.id !== 'string' || body.id.indexOf(':') === -1) {
      return Promise.resolve(init);
    }
    var variantId = body.id.split(':')[0];
    return nativeFetch(window.Shopify.routes.root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var match =
          cart.items.filter(function (i) { return i.key === body.id; })[0] ||
          cart.items.filter(function (i) { return String(i.variant_id) === variantId; })[0];
        // Not found: the line is genuinely gone. Let the original request run —
        // it 4xxs and cart.js recovers silently, which is the right outcome.
        if (!match) return init;
        body.id = match.key;
        var next = {};
        for (var k in init) if (Object.prototype.hasOwnProperty.call(init, k)) next[k] = init[k];
        next.body = JSON.stringify(body);
        return next;
      })
      .catch(function () { return init; });
  }

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();

    if (method !== 'POST' || !CART_WRITE.test(url)) return nativeFetch(input, init);

    inFlight++;
    var enqueuedAt = generation;
    var isChange = CART_CHANGE.test(url) && init && typeof init.body === 'string';

    var run = function () {
      // Ran straight away with nothing in front of it: the DOM is still fresh.
      if (!isChange || generation === enqueuedAt) {
        return nativeFetch(input, init).then(bump);
      }
      // Something else wrote to the cart while we waited — re-target first.
      return reResolve(init).then(function (fixed) {
        return nativeFetch(input, fixed).then(bump);
      });
    };

    function bump(response) { generation++; armIdleSync(); return response; }

    // Run after whatever is already queued — whether it resolved or rejected.
    var queued = chain.then(run, run);
    queued.then(
      function () { inFlight = Math.max(0, inFlight - 1); },
      function () { inFlight = Math.max(0, inFlight - 1); }
    );
    chain = afterAtMost(queued, QUEUE_TIMEOUT_MS);
    return queued;
  };

  /* ── 2. Keep the cart icon opening the drawer ─────────────────────────── */

  /* The drawer can end up showing a different cart than the server has:
     edge-cached pages carry HTML rendered for another cart state, and during
     rapid interactions section renders can apply out of order (each response
     renders asynchronously after its fetch settles, so a slower earlier render
     can clobber a newer one). Rather than fight every ordering, converge:
     compare the drawer's rendered lines (variant + quantity) against the live
     cart and re-render whenever they disagree. */
  /* Resolves true when the drawer was stale and a re-render was requested,
     false when the drawer already matched the server. */
  function syncDrawerIfStale() {
    // Surface-aware: on /cart the page list (<cart-items>) is what the shopper
    // is looking at; everywhere else it's the drawer. Scoping both the DOM
    // read and the heal to one surface avoids double-counting inputs when the
    // drawer markup also exists on the cart page.
    var onCartPage = !!document.getElementById('main-cart-items');
    var items = onCartPage
      ? document.querySelector('cart-items')
      : document.querySelector('cart-drawer-items');
    // Custom element not upgraded yet (deferred cart.js still parsing — a
    // 1–3 s window on slow phones): report STALE so the loop keeps retrying.
    // Returning "stable" here made the supervisor stand down permanently when
    // a fast shopper interacted inside that window — frozen totals, gift card
    // never appearing. Mobile-only in practice; desktop upgrades in ms.
    if (!items || typeof items.onCartUpdate !== 'function') return Promise.resolve(true);
    return nativeFetch(window.Shopify.routes.root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // Compare VARIANT TOTALS, not lines: the gift discount splits server
        // lines while the drawer renders them grouped, so per-line signatures
        // would mismatch forever and re-render in a loop.
        var domTotals = {};
        Array.prototype.forEach.call(
          document.querySelectorAll(
            onCartPage ? '#main-cart-items input[name="updates[]"]' : 'cart-drawer-items input[name="updates[]"]'
          ),
          function (input) {
            var variant = input.dataset.ffVariant || input.dataset.quantityVariantId || '?';
            domTotals[variant] = (domTotals[variant] || 0) + (parseInt(input.value, 10) || 0);
          }
        );
        var serverTotals = {};
        cart.items.forEach(function (item) {
          serverTotals[item.variant_id] = (serverTotals[item.variant_id] || 0) + item.quantity;
        });
        function sig(totals) {
          return Object.keys(totals)
            .map(function (k) { return k + ':' + totals[k]; })
            .sort()
            .join('|');
        }
        var domSig = sig(domTotals);
        var serverSig = sig(serverTotals);
        // Orphaned-gift cleanup: if the last trigger product (blanket) is gone
        // but gift-product lines remain, they've silently become PAID lines —
        // remove them rather than charging $59 for a "free" gift. The zeroing
        // goes through the write queue and re-arms this supervisor.
        var drawerEl = document.querySelector('cart-drawer');
        if (drawerEl && drawerEl.dataset.ffGiftProduct && drawerEl.dataset.ffGiftTrigger) {
          var triggerUnits = cart.items
            .filter(function (i) { return String(i.product_id) === drawerEl.dataset.ffGiftTrigger; })
            .reduce(function (a, i) { return a + i.quantity; }, 0);
          var orphans = cart.items.filter(function (i) {
            return (
              String(i.product_id) === drawerEl.dataset.ffGiftProduct &&
              i.properties && i.properties._ff_gift
            );
          });
          if (triggerUnits === 0 && orphans.length) {
            orphans.forEach(function (line) {
              window.fetch(window.Shopify.routes.root + 'cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ id: line.key, quantity: 0 })
              }).then(noop, noop);
            });
            return true; // treat as stale; the re-armed loop renders after the writes
          }
        }
        // Optimistic inputs make quantity signatures converge without a render,
        // so ALSO compare the rendered subtotal against the server — otherwise
        // line totals and the subtotal stay frozen after a burst (the reported
        // "7 blankets, still $1,047" bug).
        var subEl = onCartPage
          ? document.querySelector('#main-cart-footer .totals__total-value')
          : document.querySelector('cart-drawer .totals__total-value');
        var moneyStale = false;
        if (subEl) {
          var shownDigits = (subEl.textContent || '').replace(/[^0-9]/g, '');
          moneyStale = shownDigits !== String(cart.total_price);
        }
        if (domSig === serverSig && !moneyStale) return false;
        var drawer = document.querySelector('cart-drawer');
        if (drawer) drawer.classList.toggle('is-empty', cart.item_count === 0);
        var heal = Promise.resolve(items.onCartUpdate());
        if (onCartPage) {
          // Dawn's page-side onCartUpdate refreshes only the item list; the
          // subtotal lives in main-cart-footer and must be refreshed too.
          heal = heal.then(function () {
            items.classList.toggle('is-empty', cart.item_count === 0);
            var footer = document.getElementById('main-cart-footer');
            if (footer) footer.classList.toggle('is-empty', cart.item_count === 0);
            // The section id must be the TEMPLATE key (data-id), not the file
            // name — rendering by file name returns the section with no blocks.
            if (!footer || !footer.dataset.id) return;
            return nativeFetch(window.Shopify.routes.root + 'cart?section_id=' + footer.dataset.id)
              .then(function (r) { return r.text(); })
              .then(function (text) {
                var html = new DOMParser().parseFromString(text, 'text/html');
                var src = html.querySelector('#main-cart-footer .js-contents');
                var dst = document.querySelector('#main-cart-footer .js-contents');
                if (src && dst) dst.innerHTML = src.innerHTML;
              })
              .catch(noop);
          });
        }
        return heal.then(
          function () { return true; },
          function () { return true; }
        );
      })
      .catch(function () { return false; });
  }

  /* After the LAST queued cart write settles, supervise the drawer until it
     matches the server TWICE in a row. A single post-write sync is not enough:
     section GETs (Dawn's onCartUpdate self-heals) are not serialised, so a
     slow one can land seconds later and clobber newer DOM after a one-shot
     sync already passed. The loop re-checks with backoff and only stands down
     once the drawer is stably truthful. Any new write restarts it. */
  var idleSyncTimer = null;
  var syncRun = 0;

  function convergenceLoop(attempt, stableCount) {
    var run = syncRun;
    // NEVER render while writes are queued or the shopper is mid-burst —
    // replacing the DOM under a finger eats the next tap. Defer and retry.
    if (inFlight > 0 || Date.now() - lastTouch < 900 || ffDeltasBusy()) {
      idleSyncTimer = setTimeout(function () {
        if (run !== syncRun) return;
        convergenceLoop(attempt, stableCount);
      }, 500);
      return;
    }
    syncDrawerIfStale().then(function (wasStale) {
      if (run !== syncRun) return; // a newer write restarted supervision
      var stable = wasStale ? 0 : stableCount + 1;
      if (stable >= 2 || attempt >= 6) return;
      idleSyncTimer = setTimeout(function () {
        convergenceLoop(attempt + 1, stable);
      }, 700 + attempt * 500);
    });
  }

  function armIdleSync() {
    syncRun++;
    if (idleSyncTimer) clearTimeout(idleSyncTimer);
    var run = syncRun;
    idleSyncTimer = setTimeout(function () {
      if (run !== syncRun) return;
      convergenceLoop(0, 0);
    }, 600);
  }

  /* ── 3. Variant-delta quantity control for grouped rows ───────────────── */

  /* The FREE PILLOWCASE BXGY discount splits and re-splits blanket lines on
     every write, so line-addressed quantity maths can never be stable. Grouped
     rows (stamped data-ff-variant by the Liquid) are driven by DELTAS against
     the VARIANT instead: increases go through cart/add.js (split-immune —
     Shopify merges into the variant's lines wherever they are), decreases trim
     the variant's undiscounted lines from a fresh cart read. Discounted lines
     (the $0 gift) are never touched — the gift logic owns those. */
  /* The pre-order stamp (_ff_preorder / Pre-order, set by ff-product.js and
     ff-cart.js on 22 Oct 2026) is the one property a plain line may carry. */
  function ffPlainProps(props) {
    if (!props) return true;
    return Object.keys(props).every(function (k) { return k === '_ff_preorder' || k === 'Pre-order'; });
  }
  function ffStampOf(lines) {
    for (var i = 0; i < lines.length; i++) {
      var p = lines[i].properties;
      if (p && p._ff_preorder) return { _ff_preorder: p._ff_preorder, 'Pre-order': p['Pre-order'] || '' };
    }
    return null;
  }
  function ffUndiscountedLines(cart, variantId) {
    return cart.items.filter(function (item) {
      return (
        String(item.variant_id) === String(variantId) &&
        item.line_level_total_discount === 0 &&
        ffPlainProps(item.properties) &&
        !item.selling_plan_allocation
      );
    });
  }

  /* NOTE deliberately NOT called after each delta: re-rendering mid-burst
     replaces the DOM under the shopper's finger and eats the next tap. The
     inputs update optimistically; the convergence supervisor renders ONCE
     when the burst goes idle. */
  /* Deltas accumulate per variant and a single drain loop applies them, one
     write per cycle against a FRESH cart read. Concurrent taps therefore can
     never compute targets from the same stale snapshot (four rapid "−" taps
     used to all read qty 6 and all write qty 5). Remainders that can't be
     applied in one write go back into the pending tally for the next cycle,
     so key rehashes mid-trim can't lose or double-apply quantity. */
  var ffPending = {};
  var ffDraining = {};

  function ffDeltasBusy() {
    for (var k in ffPending) if (ffPending[k]) return true;
    for (var j in ffDraining) if (ffDraining[j]) return true;
    return false;
  }

  function ffApplyVariantDelta(variantId, delta) {
    if (!delta) return;
    ffPending[variantId] = (ffPending[variantId] || 0) + delta;
    ffDrain(variantId);
  }

  function ffDrain(variantId) {
    if (ffDraining[variantId]) return;
    ffDraining[variantId] = true;
    (function cycle() {
      var d = ffPending[variantId] || 0;
      if (!d) {
        ffDraining[variantId] = false;
        return;
      }
      ffPending[variantId] = 0;
      var op;
      if (d > 0) {
        // Shopify only merges an add into an existing line when the properties
        // match, so an increase on a stamped pre-order line must carry the same
        // stamp (otherwise the order ends up with one stamped and one plain line).
        op = nativeFetch(window.Shopify.routes.root + 'cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            var add = { id: parseInt(variantId, 10), quantity: d };
            var stamp = ffStampOf(ffUndiscountedLines(cart, variantId));
            if (stamp) add.properties = stamp;
            return window.fetch(window.Shopify.routes.root + 'cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: [add] })
            });
          });
      } else {
        op = nativeFetch(window.Shopify.routes.root + 'cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            var lines = ffUndiscountedLines(cart, variantId).sort(function (a, b) { return b.quantity - a.quantity; });
            if (!lines.length) return;
            var line = lines[0];
            var take = Math.min(line.quantity, -d);
            if (take < -d) ffPending[variantId] = (ffPending[variantId] || 0) + (d + take); // remainder next cycle
            if (!take) return;
            return window.fetch(window.Shopify.routes.root + 'cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: line.key, quantity: line.quantity - take })
            });
          });
      }
      Promise.resolve(op).then(noop, noop).then(cycle);
    })();
  }

  // Plus/minus on grouped rows: handled HERE, not by Dawn's quantity-input —
  // this listener exists from page load, so taps can't die in the window
  // between a drawer re-render and its custom elements re-initialising.
  document.addEventListener(
    'click',
    function (event) {
      var btn = event.target && event.target.closest && event.target.closest('button[name="plus"], button[name="minus"]');
      if (!btn) return;
      var wrapper = btn.closest('quantity-input') || btn.closest('.quantity');
      var input = wrapper && wrapper.querySelector('input[name="updates[]"][data-ff-variant]');
      if (!input) return; // ungrouped rows keep Dawn's behaviour
      event.stopImmediatePropagation();
      event.preventDefault();
      lastTouch = Date.now();
      var current = parseInt(input.value, 10) || 0;
      var next = Math.max(0, current + (btn.name === 'plus' ? 1 : -1));
      if (next === current) return;
      input.value = String(next);
      input.dataset.ffQty = String(next);
      ffApplyVariantDelta(input.dataset.ffVariant, next - current); // supervisor renders at idle
    },
    true
  );

  // Typed quantity values on grouped rows: delta against the last known total.
  document.addEventListener(
    'change',
    function (event) {
      var input = event.target;
      if (!input || !input.matches || !input.matches('input[name="updates[]"]') || !input.dataset.ffVariant) return;
      event.stopImmediatePropagation();
      lastTouch = Date.now();
      var next = parseInt(input.value, 10);
      var last = parseInt(input.dataset.ffQty, 10);
      if (isNaN(next) || isNaN(last)) return;
      var delta = next - last;
      if (!delta) return;
      input.dataset.ffQty = String(next); // keep rapid taps producing clean ±1 deltas
      ffApplyVariantDelta(input.dataset.ffVariant, delta); // supervisor renders at idle
    },
    true
  );

  // Remove on a grouped row must clear the WHOLE group, not one hidden line.
  document.addEventListener(
    'click',
    function (event) {
      var rm = event.target && event.target.closest && event.target.closest('cart-remove-button');
      if (!rm) return;
      var row = rm.closest('.cart-item');
      var input = row && row.querySelector('input[name="updates[]"][data-ff-variant]');
      if (!input) return; // ungrouped (e.g. gift) rows keep Dawn's behaviour
      event.stopImmediatePropagation();
      event.preventDefault();
      lastTouch = Date.now();
      var current = parseInt(input.dataset.ffQty, 10) || 0;
      input.dataset.ffQty = '0';
      row.style.opacity = '0.4'; // instant feedback; the idle render removes the row
      ffApplyVariantDelta(input.dataset.ffVariant, -current); // supervisor renders at idle
    },
    true
  );

  document.addEventListener(
    'click',
    function (event) {
      var link = event.target && event.target.closest && event.target.closest('#cart-icon-bubble');
      if (!link) return;

      var drawer = document.querySelector('cart-drawer');
      // No drawer on this page (or drawer mode off): let the link do its job.
      if (!drawer) return;

      event.preventDefault();

      if (typeof drawer.open === 'function') {
        drawer.open(link);
        syncDrawerIfStale();
        return;
      }
      // Element present but not yet upgraded — wait for it rather than navigate.
      if (window.customElements && customElements.whenDefined) {
        customElements.whenDefined('cart-drawer').then(function () {
          if (typeof drawer.open === 'function') {
            drawer.open(link);
            syncDrawerIfStale();
          } else {
            window.location.href = link.getAttribute('href');
          }
        });
      }
    },
    true // capture: beat both Dawn's handler and the browser's navigation
  );

  /* ── 5. Checkout must never race pending quantity writes ────────────────
     Deltas drain sequentially (fresh read + write per cycle) — seconds of
     tail on phone RTT. Navigating to checkout mid-drain kills the queue and
     checkout renders the OLDER server cart. Hold the tap until the pipeline
     is quiet, then re-fire it so Dawn's own submit semantics (cart note,
     form fields) are preserved. */
  document.addEventListener(
    'click',
    function (event) {
      var btn =
        event.target &&
        event.target.closest &&
        event.target.closest('button[name="checkout"], #CartDrawer-Checkout, a[href$="/checkout"]');
      if (!btn) return;
      if (btn.dataset.ffFlushed === '1') {
        delete btn.dataset.ffFlushed;
        return; // second pass after the flush — let it through
      }
      if (btn.getAttribute('aria-busy') === 'true') {
        // Already flushing — swallow repeat taps instead of stacking waiters.
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      if (!ffDeltasBusy() && inFlight === 0) return; // pipeline quiet — proceed
      event.preventDefault();
      event.stopImmediatePropagation();
      var started = Date.now();
      btn.setAttribute('aria-busy', 'true');
      (function waitQuiet() {
        var quiet = !ffDeltasBusy() && inFlight === 0;
        if (!quiet && Date.now() - started < 6000) {
          setTimeout(waitQuiet, 150);
          return;
        }
        btn.removeAttribute('aria-busy');
        if (btn.isConnected) {
          btn.dataset.ffFlushed = '1';
          btn.click();
        } else {
          // Drawer re-rendered while waiting — the original node is gone.
          window.location.href = window.Shopify.routes.root + 'checkout';
        }
      })();
    },
    true
  );

  /* Late custom-element upgrade (deferred cart.js on slow phones): re-arm the
     supervisor once the drawer machinery exists, in case a burst happened
     entirely inside the pre-upgrade window. */
  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('cart-drawer-items').then(armIdleSync, noop);
  }
})();
